import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MissionHeader = ({ mission, onReset, onToggleInstructions, showInstructions }) => {
  const navigate = useNavigate();

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'text-success bg-success/10';
      case 'intermediate':
        return 'text-warning bg-warning/10';
      case 'advanced':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted/20';
    }
  };

  return (
    <div className="bg-card border-b border-subtle p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            {/* Back to Missions Button */}
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/mission-map-dashboard')}
              className="text-muted-foreground hover:text-foreground"
            >
              Back to Missions
            </Button>
            <div className="w-px h-6 bg-border" />
            <h1 className="text-xl font-semibold text-foreground">
              {mission?.title}
            </h1>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission?.difficulty)}`}>
              {mission?.difficulty}
            </span>
            
            {/* Progress Bar moved here - beside the mission title */}
            <div className="flex items-center space-x-2 ml-4">
              <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-success transition-all duration-300"
                  style={{ width: `${mission?.progress || 0}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {mission?.progress || 0}%
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-3 ml-24">
            {mission?.description}
          </p>
          <div className="flex items-center space-x-4 text-xs text-muted-foreground ml-24">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>Est. {mission?.estimatedTime}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Target" size={14} />
              <span>{mission?.points} points</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} />
              <span>{mission?.completedBy} completed</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={onReset}
          >
            Reset Code
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName={showInstructions ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            onClick={onToggleInstructions}
          >
            Instructions
          </Button>
        </div>
      </div>
      
      {/* Instructions panel - now collapsible and properly sized */}
      {showInstructions && (
        <div className="bg-muted/30 rounded-lg p-4 animate-slide-down">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Objective</h3>
              <p className="text-sm text-muted-foreground">
                {mission?.objective}
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Requirements</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                {mission?.requirements?.map((req, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircle2" size={14} className="mt-0.5 text-success flex-shrink-0" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Hints</h3>
              <div className="space-y-2">
                {mission?.hints?.map((hint, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Icon name="Lightbulb" size={14} className="mt-0.5 text-warning flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{hint}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-4 pt-2 border-t border-subtle">
              <a
                href={mission?.documentationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <Icon name="ExternalLink" size={14} />
                <span>Documentation</span>
              </a>
              <a
                href={mission?.exampleLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <Icon name="Code" size={14} />
                <span>Examples</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionHeader;